import { CommonModule } from '@angular/common';
import {
    AfterContentInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChild,
    ElementRef,
    EventEmitter,
    Inject,
    InjectionToken,
    Input,
    OnChanges,
    OnDestroy,
    Optional,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewRef
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Logger } from '@df/core/service/logger';
import { TimeoutService } from '@df/ng/timeout.service';
import { toDate } from '@df/util/fn/to-date';
import each from 'lodash-es/each';
import { BehaviorSubject } from 'rxjs';

export interface IScore {
    years?: number | string;
    months?: number | string;
    days?: number | string;
    hours?: number | string;
    minutes?: number | string;
    seconds?: number | string;
}

export interface IResult {
    text?: string;
    unit?: string | number;
    score?: IScore;
}

export interface TimeUnit {
    plural: string;
    singular: string;
    divider: number;
    digits?: number;
}

export const TIME_UNITS = new InjectionToken<TimeUnit[]>('TimeUnits');

export const TIME_UNITS_DEFAULT = [
    { plural: 'years', singular: 'year', divider: 31104000000 },
    { plural: 'months', singular: 'month', divider: 2592000000 },
    { plural: 'days', singular: 'day', divider: 86400000 },
    { plural: 'hours', singular: 'hour', divider: 3600000, digits: 2 },
    { plural: 'minutes', singular: 'minute', divider: 60000, digits: 2 },
    { plural: 'seconds', singular: 'second', divider: 1000, digits: 2 }
];

export type ICountdownUnitPlular = 'years' | 'mounts' | 'days' | 'hours' | 'minutes' | 'seconds';
export type ICountdownUnitSingular = 'year' | 'mount' | 'day' | 'hour' | 'minute' | 'second';

/**
 * @usage
 * ```html
 *
 * <countdown date="2020-12-12"
 *            shouldPad="true">
 *      Here you use custom templates
 *      <ng-template #countdownTemplate
 *                   let-result>
 *          {{result.score?.seconds}}
 *      </ng-template>
 * </countdown>
 *
 * ```
 *
 */
@Component({
    selector: 'countdown',
    template: `
        <ng-template #defaultTemplate>
            @if (isCountdown) {
                {{ result.score?.hours }} : {{ result.score?.minutes }} : {{ result.score?.seconds }}
            } @else {
                {{ result.text }}
            }
            @if (result.unit) {
                {{ result.unit }}
            }
        </ng-template>
        <ng-container *ngTemplateOutlet="countdownTemplate || defaultTemplate; context: { $implicit: result }" />
    `,
    standalone: true,
    imports: [CommonModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CountdownComponent implements OnChanges, OnDestroy, AfterContentInit {
    private allTimeUnits: TimeUnit[] = TIME_UNITS_DEFAULT;

    @ContentChild('countdownTemplate', { static: false })
    countdownTemplate!: TemplateRef<any>;

    private isActive$ = new BehaviorSubject(false);

    @Output()
    readonly isActive = new EventEmitter<boolean>();

    constructor(
        public element: ElementRef,
        private changeDetectorRef: ChangeDetectorRef,
        private timeoutService: TimeoutService,
        private logger: Logger,
        @Optional() @Inject(TIME_UNITS) timeUnits: TimeUnit[]
    ) {
        if (timeUnits) {
            this.allTimeUnits = timeUnits;
        }

        this.isActive$.pipe(takeUntilDestroyed()).subscribe(state => {
            if (state) {
                // will not trigger change detection
                if (!this.intervalId) {
                    this.intervalId = this.timeoutService.setInterval(() => this.update(), 1000);
                }
                this.update();
            } else {
                this.cleanup();
            }
            this.isActive.emit(state);
        });
    }

    // Date (either date or time is required)
    @Input()
    date?: Date;

    // Timestamp  (either date or time is required)
    @Input()
    time?: number;

    // Optional param for endsin format
    @Input()
    forceUnit?: ICountdownUnitPlular | ICountdownUnitSingular;

    // Default 2, how many units should be shown
    @Input()
    stringDepth = '2';

    // True will cause countdown mode
    @Input()
    isCountdown?: boolean;

    // If true set then will pad secods, minutes, hours
    @Input()
    shouldPad?: boolean;

    /**
     * Pass allowed units like ['days', 'hours','minutes' ]
     */
    @Input()
    whitelist?: string[];

    // units index to provde them quicker
    private _unitMap = {};
    private intervalId?: number;
    result: IResult = {};

    private diffMs?: number;

    ngOnInit() {
        this.update();
        this.isActive.emit(this.isActive$.value);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['time'] || changes['date']) {
            if (this.time) {
                this.date = new Date(this.time * 1000);
            } else {
                this.date = toDate(this.date);
            }
        }
        this.update();
    }

    ngOnDestroy() {
        this.cleanup();
    }

    private cleanup() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = undefined;
        }
    }

    private get timeUnits() {
        if (this.whitelist) {
            return this.allTimeUnits.filter(i => this.whitelist!.includes(i.plural));
        }
        return this.allTimeUnits;
    }

    private getDiff(): number {
        const now = Date.now();

        if (this.date) {
            // Calculating difference between current date and target date.
            this.diffMs = this.date.getTime() - now;

            // Making sure difference is adjusted correctly if timer should be stopped.
            if (this.diffMs < 0) {
                return Math.max(this.diffMs, 0);
            }
            return this.diffMs;
        }
        return 0;
    }

    ngAfterContentInit() {
        this.update();
    }

    private update(): void {
        const diffMs = this.getDiff();
        if (diffMs) {
            this.result = this.diffMsToTimeUnits(diffMs);
            this.setActive(true);
        } else {
            this.result.score = undefined;
            this.setActive(false);
        }
        this.detectChanges();
    }

    private setActive(value: boolean) {
        if (this.isActive$.value !== value) {
            this.isActive$.next(value);
        }
    }

    private detectChanges(): void {
        if (!(<ViewRef>this.changeDetectorRef).destroyed) {
            this.changeDetectorRef.detectChanges();
        }
    }

    private diffMsToTimeUnits(diffMs): IResult {
        const result: IResult = {};
        if (this.forceUnit) {
            const unit = this.getUnit(this.forceUnit);

            if (unit) {
                const value = Math.ceil(diffMs / unit.divider);
                result.unit = value;
            } else {
                this.logger.error('Invalid unit for countdown: [' + this.forceUnit + ']');
            }
        } else {
            result.score = {};
            this.timeUnits.forEach(unit => {
                let value;

                if (diffMs > 0) {
                    value = Math.floor(diffMs / unit.divider);
                    diffMs -= value * unit.divider;
                } else {
                    value = 0;
                }
                if (result.score) {
                    result.score[unit.plural] = this.shouldPad && unit.digits ? this.pad(value, unit.digits) : value;
                }
            });
        }

        result.text = this.toString();
        return result;
    }

    private toString(): string {
        const result = this.result.score;
        if (!result) {
            return '';
        }
        let depth = parseInt(this.stringDepth, 10) || 2;
        let text = '';

        if (this.isCountdown && !result.years && !result.months && !result.days) {
            return [this.pad(result.hours, 2), this.pad(result.minutes, 2), this.pad(result.seconds, 2)].join(':');
        } else {
            each(result, (value, key) => {
                if (value) {
                    const unit = this.getUnit(key)!;
                    if (text) {
                        text += ' ';
                    }
                    text += value + ' ' + (value === 1 ? unit.singular : unit.plural);
                }
                if (value || text) {
                    if (!--depth) {
                        return false;
                    }
                }
            });
        }

        return text;
    }

    private pad(num, size): string {
        let s = num + '';
        while (s.length < size) {
            s = '0' + s;
        }
        return s;
    }

    private getUnit(id: ICountdownUnitPlular | ICountdownUnitSingular | string): TimeUnit | undefined {
        if (!this._unitMap[id]) {
            this._unitMap[id] = this.timeUnits.find(i => i.plural === id || i.singular === id);
        }
        return this._unitMap[id];
    }
}
